import { useContext, useEffect, useRef, useCallback, useState } from "react";
import type { GetSwiggyHeadersResponsePayload } from "@swiggy-private/native-bridge";
import { IPLWidgetControllerCards } from "@swiggy-private/dls-react-mxn/dist/WidgetController/PLWidgetController/type";
import {
    usePlApiContextFetch,
    ResponseContext,
    QrCodeActions,
    TableContext,
    IApiConfigs,
    BottomBarContext,
} from "@swiggy-private/portal-dineout-context";
import { LOG_TABLES } from "../constants";
import { EMPTY_TRACK_TYPES, TRACK_ORDER_ENDPOINT } from "../constants/track";

interface TrackDataResponse {
    cards: IPLWidgetControllerCards[];
    configs?: IApiConfigs;
}

interface UseFetchTrackDataProps {
    apiHost: string;
    headers: GetSwiggyHeadersResponsePayload;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getCardDataByType: (cards: Array<any>, type: string) => Array<any> | null;
}

export interface UseFetchTrackDataResult {
    fetchTrackData: (isActive: () => boolean) => Promise<boolean>;
    showEmptyOrderError: boolean;
    tableId: string;
    restaurantId: string;
}

/**
 * Hook that encapsulates the track data fetching logic
 */
export const useFetchTrackData = ({
    apiHost,
    headers,
    getCardDataByType,
}: UseFetchTrackDataProps): UseFetchTrackDataResult => {
    const { tableId = "", restaurantId = "", parentOrderId = "" } = useContext(TableContext);
    const { shouldFetchPartnerOrder, setFetchPartnerOrder } = useContext(BottomBarContext);
    const fetchPL = usePlApiContextFetch();
    const { dispatch } = useContext(ResponseContext);
    const [showEmptyOrderError, setShowEmptyOrderError] = useState(false);

    // Keep frequently changing values in ref to avoid stale closures in async callbacks
    // Stable references (dispatch, fetchPL) are used directly
    const latestRef = useRef({
        apiHost,
        headers,
        tableId,
        restaurantId,
        parentOrderId,
        getCardDataByType,
        shouldFetchPartnerOrder,
    });

    useEffect(() => {
        latestRef.current = {
            apiHost,
            headers,
            tableId,
            restaurantId,
            parentOrderId,
            getCardDataByType,
            shouldFetchPartnerOrder,
        };
    });

    const fetchTrackData = useCallback(
        async (isActive: () => boolean): Promise<boolean> => {
            if (!isActive()) return false;

            const {
                apiHost: currentApiHost,
                headers: currentHeaders,
                tableId: currentTableId,
                restaurantId: currentRestaurantId,
                parentOrderId: currentParentOrderId,
                getCardDataByType: currentGetCardDataByType,
                shouldFetchPartnerOrder: currentFetchPartnerOrder,
            } = latestRef.current;

            try {
                const data = await fetchPL?.<TrackDataResponse>(
                    {
                        apiHost: currentApiHost,
                        endpoint: TRACK_ORDER_ENDPOINT,
                        retryCount: 3,
                        searchParams: {
                            tableId: currentTableId,
                            restaurantId: currentRestaurantId,
                            parentOrderId: currentParentOrderId,
                            shouldFetchPartnerOrder: String(currentFetchPartnerOrder),
                        },
                        options: {
                            headers: currentHeaders as HeadersInit,
                        },
                    },
                    currentHeaders,
                    () => {},
                    LOG_TABLES
                );

                if (!isActive()) return false;

                if (!data?.success?.cards) return false;

                const fetchedCards = data.success.cards;
                const hasValidTypes = EMPTY_TRACK_TYPES.some((type) => {
                    const result = currentGetCardDataByType(fetchedCards, type);
                    // Check if result exists and is not an empty array
                    return result && (!Array.isArray(result) || result.length > 0);
                });

                if (!hasValidTypes) {
                    setShowEmptyOrderError(true);
                    return true;
                }

                setShowEmptyOrderError(false);
                dispatch({
                    type: QrCodeActions.TRACK_SCREEN_SUCCESS,
                    payload: { data },
                });
                setFetchPartnerOrder(false);

                return true;
            } catch (_) {
                if (isActive()) {
                    dispatch({
                        type: QrCodeActions.TRACK_SCREEN_ERROR,
                        payload: {},
                    });
                }
                return false;
            }
        },
        [dispatch, fetchPL, setFetchPartnerOrder]
    );

    return {
        fetchTrackData,
        showEmptyOrderError,
        tableId,
        restaurantId,
    };
};
